import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { useProperty } from "@/lib/property";
import { useIsMaster, useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { INV } from "@/constants/testIds";
import { ReceiptIcon, PlusIcon, PrinterIcon, PencilSimpleIcon, TrashIcon, XIcon, EnvelopeIcon, WhatsappLogoIcon, FilePdfIcon, MagnifyingGlassIcon, FunnelIcon, CaretUpIcon, CaretDownIcon, FilesIcon } from "@phosphor-icons/react";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

function SortableHead({ sort, onClick, sortKey, label, tid }) {
  const active = sort.key === sortKey;
  return (
    <TableHead>
      <button
        type="button"
        onClick={() => onClick(sortKey)}
        className={`group inline-flex items-center gap-1 select-none hover:text-foreground transition-colors ${active ? "text-foreground" : "text-muted-foreground"}`}
        data-testid={tid}
        aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
      >
        {label}
        <span className="inline-flex flex-col leading-none text-[9px] opacity-70 group-hover:opacity-100">
          <CaretUpIcon size={8} weight={active && sort.dir === "asc" ? "fill" : "regular"} className={active && sort.dir === "asc" ? "text-primary" : ""} />
          <CaretDownIcon size={8} weight={active && sort.dir === "desc" ? "fill" : "regular"} className={active && sort.dir === "desc" ? "text-primary" : ""} />
        </span>
      </button>
    </TableHead>
  );
}

function EditInvoiceDialog({ invoice, onSaved, onClose }) {
  const [form, setForm] = useState(() => ({
    number: invoice.number,
    room_charges: invoice.room_charges,
    tax_rate: invoice.tax_rate ?? 12,
    tax_inclusive: invoice.tax_inclusive ?? false,
    extra_charges: invoice.extra_charges || 0,
    discount: invoice.discount || 0,
    notes: invoice.notes || "",
    line_items: invoice.line_items?.length ? invoice.line_items : [],
  }));
  const [saving, setSaving] = useState(false);

  const addLine = () => setForm((f) => ({ ...f, line_items: [...f.line_items, { description: "", qty: 1, rate: 0, amount: 0 }] }));
  const removeLine = (i) => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, idx) => idx !== i) }));
  const patchLine = (i, patch) => setForm((f) => ({
    ...f,
    line_items: f.line_items.map((li, idx) => idx === i ? { ...li, ...patch, amount: (patch.qty ?? li.qty) * (patch.rate ?? li.rate) } : li),
  }));

  const lineTotal = form.line_items.reduce((s, li) => s + Number(li.qty || 0) * Number(li.rate || 0), 0);
  const subtotal = Number(form.room_charges || 0) + Number(form.extra_charges || 0) + lineTotal;
  const discounted = Math.max(0, subtotal - Number(form.discount || 0));
  const rate = Number(form.tax_rate || 0);
  let tax = 0, grand = 0, taxable = 0;
  if (form.tax_inclusive) {
    taxable = rate > 0 ? +(discounted / (1 + rate / 100)).toFixed(2) : discounted;
    tax = +(discounted - taxable).toFixed(2);
    grand = +discounted.toFixed(2);
  } else {
    taxable = discounted;
    tax = +(discounted * rate / 100).toFixed(2);
    grand = +(discounted + tax).toFixed(2);
  }

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        room_charges: Number(form.room_charges),
        tax_rate: Number(form.tax_rate),
        tax_inclusive: !!form.tax_inclusive,
        extra_charges: Number(form.extra_charges),
        discount: Number(form.discount),
        line_items: form.line_items.map((li) => ({ ...li, qty: Number(li.qty), rate: Number(li.rate), amount: Number(li.qty) * Number(li.rate) })),
      };
      await api.patch(`/invoices/${invoice.id}`, payload);
      toast.success("Invoice updated");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  return (
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle>Edit invoice · {invoice.number}</DialogTitle></DialogHeader>
      <form onSubmit={save} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Invoice number</Label><Input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} data-testid="edit-invoice-number" /></div>
          <div><Label>Tax rate (%)</Label><Input type="number" step="0.5" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} data-testid="edit-invoice-tax-rate" /></div>
        </div>

        <div className="rounded-md border border-border bg-muted/30 p-3">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">GST treatment</Label>
          <div className="mt-2 grid grid-cols-2 gap-2" data-testid="edit-invoice-tax-mode">
            <button
              type="button"
              onClick={() => setForm({ ...form, tax_inclusive: false })}
              className={`text-left rounded-md border p-3 text-sm transition ${!form.tax_inclusive ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}
              data-testid="edit-invoice-tax-exclusive"
            >
              <div className="font-medium">Exclusive of GST</div>
              <div className="text-xs text-muted-foreground mt-0.5">GST is added on top of the base amount.</div>
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, tax_inclusive: true })}
              className={`text-left rounded-md border p-3 text-sm transition ${form.tax_inclusive ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}
              data-testid="edit-invoice-tax-inclusive"
            >
              <div className="font-medium">Inclusive of GST</div>
              <div className="text-xs text-muted-foreground mt-0.5">Base amount already contains GST; back-calculated.</div>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div><Label>Room charges (₹)</Label><Input type="number" step="0.01" value={form.room_charges} onChange={(e) => setForm({ ...form, room_charges: e.target.value })} data-testid="edit-invoice-room-charges" /></div>
          <div><Label>Extras (₹)</Label><Input type="number" step="0.01" value={form.extra_charges} onChange={(e) => setForm({ ...form, extra_charges: e.target.value })} /></div>
          <div><Label>Discount (₹)</Label><Input type="number" step="0.01" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} data-testid="edit-invoice-discount" /></div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>Line items (food, laundry, spa, etc.)</Label>
            <Button type="button" size="sm" variant="outline" onClick={addLine} data-testid="edit-invoice-add-line"><PlusIcon size={12} className="mr-1" />Add line</Button>
          </div>
          {form.line_items.length === 0 && <p className="text-xs text-muted-foreground italic">No line items. Add ancillary charges above.</p>}
          <div className="space-y-2">
            {form.line_items.map((li, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-6"><Input placeholder="Description" value={li.description} onChange={(e) => patchLine(i, { description: e.target.value })} /></div>
                <div className="col-span-2"><Input type="number" step="0.5" placeholder="Qty" value={li.qty} onChange={(e) => patchLine(i, { qty: Number(e.target.value) })} /></div>
                <div className="col-span-2"><Input type="number" step="0.01" placeholder="Rate" value={li.rate} onChange={(e) => patchLine(i, { rate: Number(e.target.value) })} /></div>
                <div className="col-span-1 text-right text-sm font-mono-plex">{inr(li.qty * li.rate)}</div>
                <div className="col-span-1"><Button type="button" size="icon" variant="ghost" onClick={() => removeLine(i)}><XIcon size={14} /></Button></div>
              </div>
            ))}
          </div>
        </div>

        <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Payment terms, thank-you note, etc." /></div>

        <div className="rounded-md border border-border bg-muted/40 p-4 space-y-1 text-sm" data-testid="edit-invoice-totals">
          <div className="flex justify-between"><span className="text-muted-foreground">Room + extras + lines ({form.tax_inclusive ? "incl." : "excl."} GST)</span><span className="font-mono-plex">{inr(subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Discount</span><span className="font-mono-plex">– {inr(form.discount)}</span></div>
          {form.tax_inclusive && (
            <div className="flex justify-between"><span className="text-muted-foreground">Taxable value</span><span className="font-mono-plex">{inr(taxable)}</span></div>
          )}
          <div className="flex justify-between"><span className="text-muted-foreground">GST ({form.tax_rate}%) {form.tax_inclusive ? "— inclusive" : ""}</span><span className="font-mono-plex">{inr(tax)}</span></div>
          <div className="flex justify-between text-base pt-2 border-t border-border/60"><span className="font-medium">Grand total</span><span className="font-display text-xl">{inr(grand)}</span></div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving} data-testid="edit-invoice-save-btn">{saving ? "Saving…" : "Save changes"}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

export default function Invoices() {
  const { activeId } = useProperty();
  const isMaster = useIsMaster();
  const { user } = useAuth() || {};
  const isAdmin = (user?.role || "") === "admin";
  const [items, setItems] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [platform, setPlatform] = useState({ default_tax_rate: 12, default_tax_inclusive: false });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resId, setResId] = useState("");
  const [corporate, setCorporate] = useState({ enabled: false, name: "", gstin: "", address: "", pan: "" });  const [extra, setExtra] = useState(0);
  const [genTaxRate, setGenTaxRate] = useState(12);
  const [genInclusive, setGenInclusive] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });

  const toggleSort = (key) => {
    setSort((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  };

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = items.filter((inv) => {
      if (statusFilter !== "all" && inv.status !== statusFilter) return false;
      if (dateFrom || dateTo) {
        const d = (inv.created_at || "").slice(0, 10);
        if (dateFrom && d < dateFrom) return false;
        if (dateTo && d > dateTo) return false;
      }
      if (!q) return true;
      const hay = [
        inv.number,
        inv.guest?.name,
        inv.guest?.phone,
        inv.room_number,
        inv.room_type,
        inv.notes,
      ].filter(Boolean).map((v) => String(v).toLowerCase()).join(" ");
      return hay.includes(q);
    });
    // Sort
    const getVal = (inv, key) => {
      switch (key) {
        case "number": return inv.number || "";
        case "booking_ref": return inv.booking_ref || "";
        case "guest": return (inv.guest?.name || "").toLowerCase();
        case "room_number": return String(inv.room_number || "");
        case "room_charges": return Number(inv.room_charges || 0);
        case "tax_amount": return Number(inv.tax_amount || 0);
        case "extra_charges": return Number(inv.extra_charges || 0);
        case "discount": return Number(inv.discount || 0);
        case "deposit_applied": return Number(inv.deposit_applied || 0);
        case "total": return Number(inv.total || 0);
        case "balance_due": return Number(inv.balance_due || 0);
        case "status": return inv.status || "";
        case "created_at":
        default: return inv.created_at || "";
      }
    };
    const sorted = [...base].sort((a, b) => {
      const av = getVal(a, sort.key);
      const bv = getVal(b, sort.key);
      // Numeric-aware comparison for numeric strings like room numbers
      if (typeof av === "number" && typeof bv === "number") return sort.dir === "asc" ? av - bv : bv - av;
      const na = Number(av), nb = Number(bv);
      if (!Number.isNaN(na) && !Number.isNaN(nb) && String(av).trim() && String(bv).trim() && av === String(na) && bv === String(nb)) {
        return sort.dir === "asc" ? na - nb : nb - na;
      }
      const s = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
      return sort.dir === "asc" ? s : -s;
    });
    return sorted;
  }, [items, search, statusFilter, dateFrom, dateTo, sort]);

  const clearFilters = () => { setSearch(""); setStatusFilter("all"); setDateFrom(""); setDateTo(""); };
  const hasActiveFilter = search || statusFilter !== "all" || dateFrom || dateTo;

  const load = () => {
    if (!activeId) return;
    api.get("/invoices", { params: { property_id: activeId } }).then((r) => setItems(r.data));
    api.get("/reservations", { params: { property_id: activeId } }).then((r) => setReservations(r.data));
    api.get("/settings/platform").then((r) => {
      setPlatform(r.data);
      setGenTaxRate(r.data.default_tax_rate ?? 12);
      setGenInclusive(!!r.data.default_tax_inclusive);
    });
  };
  useEffect(() => { load(); }, [activeId]);

  const generate = async (e) => {
    e.preventDefault();
    if (!resId) return;
    try {
      const r = await api.post(`/invoices/generate/${resId}`, null, {
        params: {
          extra_charges: Number(extra) || 0,
          tax_rate: Number(genTaxRate) || 0,
          tax_inclusive: genInclusive,
        },
      });
      // If corporate billing turned on, patch the newly created invoice
      if (corporate.enabled && r.data?.id) {
        try {
          await api.patch(`/invoices/${r.data.id}`, {
            bill_to_corporate: true,
            corporate_name: corporate.name,
            corporate_gstin: corporate.gstin,
            corporate_address: corporate.address,
            corporate_pan: corporate.pan,
          });
        } catch (err) { toast.error("Corporate details failed to save"); }
      }
      toast.success("Invoice generated");
      setOpen(false);
      setResId(""); setExtra(0);
      setCorporate({ enabled: false, name: "", gstin: "", address: "", pan: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const [payDialog, setPayDialog] = useState(null); // {inv, mode, reference}

  const openMarkPaid = (inv) => setPayDialog({ inv, mode: "cash", reference: "" });
  const confirmMarkPaid = async () => {
    if (!payDialog) return;
    try {
      await api.post(`/invoices/${payDialog.inv.id}/mark-paid`, {
        amount: payDialog.inv.total,
        payment_mode: payDialog.mode,
        payment_reference: (payDialog.reference || "").trim() || null,
      });
      toast.success(`Marked paid via ${payDialog.mode.toUpperCase()}`);
      setPayDialog(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.number}?`)) return;
    try { await api.delete(`/invoices/${inv.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const print = (inv, split = false) => {
    // Open the exact same PDF that is emailed / WhatsApp'd so the customer
    // always sees the same document across every channel.
    const token = localStorage.getItem("pms_token");
    const qs = split ? "?split=1" : "";
    fetch(`${API}/invoices/${inv.id}/pdf${qs}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
        const w = window.open(url, "_blank", "noopener");
        if (!w) toast.error("Popup blocked — allow popups and retry.");
        // Revoke after a delay so the tab has time to load the PDF
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      })
      .catch(() => toast.error("Print failed"));
  };

  const downloadPdf = (inv, split = false) => {
    const token = localStorage.getItem("pms_token");
    const qs = split ? "?split=1" : "";
    fetch(`${API}/invoices/${inv.id}/pdf${qs}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = split ? `${inv.number}-split.pdf` : `${inv.number}.pdf`;
        link.click();
        URL.revokeObjectURL(link.href);
      })
      .catch(() => toast.error("PDF download failed"));
  };

  const sendEmail = async (inv) => {
    try {
      await api.post(`/invoices/${inv.id}/send-email`, { base_url: window.location.origin });
      toast.success(`Emailed invoice ${inv.number}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Email failed"); }
  };
  const sendWhatsApp = async (inv) => {
    try {
      await api.post(`/invoices/${inv.id}/send-whatsapp`, { base_url: window.location.origin });
      toast.success(`WhatsApp sent for ${inv.number}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "WhatsApp failed"); }
  };

  return (
    <div>
      <PageHeader
        overline="Billing"
        title="Invoices"
        description="Generate, edit and print GST invoices. Record payments manually (cash, UPI, card, bank)."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid={INV.generateBtn}><PlusIcon size={16} className="mr-1" />Generate invoice</Button></DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Generate invoice</DialogTitle></DialogHeader>
              <form onSubmit={generate} className="space-y-4">
                <div>
                  <Label>Reservation</Label>
                  <Select value={resId} onValueChange={setResId}>
                    <SelectTrigger><SelectValue placeholder="Pick reservation" /></SelectTrigger>
                    <SelectContent>
                      {reservations.filter((r) => ["checked_in", "checked_out", "booked"].includes(r.status)).map((r) => (
                        <SelectItem key={r.id} value={r.id}>{r.guest?.name} · Room {r.room?.number} · {r.check_in} → {r.check_out}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Extra charges (₹)</Label>
                    <Input type="number" value={extra} onChange={(e) => setExtra(e.target.value)} data-testid="gen-invoice-extra" />
                  </div>
                  <div>
                    <Label>GST rate (%)</Label>
                    <Input type="number" step="0.5" value={genTaxRate} onChange={(e) => setGenTaxRate(e.target.value)} data-testid="gen-invoice-tax-rate" />
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/30 p-3">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">GST treatment</Label>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setGenInclusive(false)}
                      className={`text-left rounded-md border p-3 text-sm transition ${!genInclusive ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}
                      data-testid="gen-invoice-tax-exclusive"
                    >
                      <div className="font-medium">Exclusive of GST</div>
                      <div className="text-xs text-muted-foreground mt-0.5">Add {genTaxRate}% on top of the room rate.</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setGenInclusive(true)}
                      className={`text-left rounded-md border p-3 text-sm transition ${genInclusive ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}
                      data-testid="gen-invoice-tax-inclusive"
                    >
                      <div className="font-medium">Inclusive of GST</div>
                      <div className="text-xs text-muted-foreground mt-0.5">Room rate already contains {genTaxRate}%.</div>
                    </button>
                  </div>
                </div>
                {/* Corporate billing */}
                <div className="rounded-md border border-border p-3" data-testid="gen-invoice-corporate-block">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={corporate.enabled} onChange={(e) => setCorporate({ ...corporate, enabled: e.target.checked })} className="accent-primary" data-testid="gen-invoice-corporate-toggle" />
                    <span className="font-medium">Bill to a company (Corporate GST invoice)</span>
                  </label>
                  {corporate.enabled && (
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <div className="col-span-2"><Label className="text-xs">Company name</Label><Input value={corporate.name} onChange={(e) => setCorporate({ ...corporate, name: e.target.value })} data-testid="gen-invoice-corp-name" /></div>
                      <div><Label className="text-xs">GSTIN</Label><Input value={corporate.gstin} onChange={(e) => setCorporate({ ...corporate, gstin: e.target.value })} placeholder="15-digit GSTIN" data-testid="gen-invoice-corp-gstin" /></div>
                      <div><Label className="text-xs">PAN (optional)</Label><Input value={corporate.pan} onChange={(e) => setCorporate({ ...corporate, pan: e.target.value })} /></div>
                      <div className="col-span-2"><Label className="text-xs">Billing address</Label><Input value={corporate.address} onChange={(e) => setCorporate({ ...corporate, address: e.target.value })} /></div>
                    </div>
                  )}
                </div>
                <DialogFooter><Button type="submit" data-testid="gen-invoice-submit">Generate ({genInclusive ? "incl." : "excl."} {genTaxRate}% GST)</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />      <Card className="p-3 mb-4 flex flex-wrap gap-2 items-center" data-testid="invoice-filter-bar">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlassIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by number, guest, phone or room…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="invoice-search"
          />
        </div>
        <div className="flex items-center gap-1">
          <Label htmlFor="inv-date-from" className="text-[10px] uppercase tracking-widest text-muted-foreground">From</Label>
          <Input id="inv-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-36" data-testid="invoice-date-from" />
        </div>
        <div className="flex items-center gap-1">
          <Label htmlFor="inv-date-to" className="text-[10px] uppercase tracking-widest text-muted-foreground">To</Label>
          <Input id="inv-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-36" data-testid="invoice-date-to" />
        </div>
        <div className="flex items-center gap-2">
          <FunnelIcon size={14} className="text-muted-foreground" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36" data-testid="invoice-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {hasActiveFilter && (
          <Button type="button" variant="ghost" size="sm" onClick={clearFilters} data-testid="invoice-clear-filters"><XIcon size={12} className="mr-1" />Clear</Button>
        )}
        <span className="text-xs text-muted-foreground ml-auto" data-testid="invoice-result-count">
          {filtered.length} of {items.length}
        </span>
      </Card>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHead sort={sort} onClick={toggleSort} sortKey="number" label="#" tid="invoice-sort-number" />
              <SortableHead sort={sort} onClick={toggleSort} sortKey="booking_ref" label="Booking" tid="invoice-sort-booking" />
              <SortableHead sort={sort} onClick={toggleSort} sortKey="guest" label="Guest" tid="invoice-sort-guest" />
              <SortableHead sort={sort} onClick={toggleSort} sortKey="room_number" label="Room #" tid="invoice-sort-room" />
              <SortableHead sort={sort} onClick={toggleSort} sortKey="room_charges" label="Room charges" tid="invoice-sort-charges" />
              <SortableHead sort={sort} onClick={toggleSort} sortKey="tax_amount" label="Tax" tid="invoice-sort-tax" />
              <SortableHead sort={sort} onClick={toggleSort} sortKey="extra_charges" label="Extras" tid="invoice-sort-extras" />
              <SortableHead sort={sort} onClick={toggleSort} sortKey="discount" label="Discount" tid="invoice-sort-discount" />
              <SortableHead sort={sort} onClick={toggleSort} sortKey="deposit_applied" label="Deposit" tid="invoice-sort-deposit" />
              <SortableHead sort={sort} onClick={toggleSort} sortKey="total" label="Total" tid="invoice-sort-total" />
              <SortableHead sort={sort} onClick={toggleSort} sortKey="balance_due" label="Balance" tid="invoice-sort-balance" />
              <SortableHead sort={sort} onClick={toggleSort} sortKey="created_at" label="Date" tid="invoice-sort-date" />
              <SortableHead sort={sort} onClick={toggleSort} sortKey="status" label="Status" tid="invoice-sort-status" />
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((inv) => (
              <TableRow key={inv.id} data-testid={`invoice-row-${inv.number}`}>
                <TableCell className="font-mono-plex text-xs">{inv.number}</TableCell>
                <TableCell className="font-mono-plex text-[11px]" data-testid={`invoice-booking-${inv.number}`}>
                  {inv.booking_ref ? <span className="text-primary">{inv.booking_ref}</span> : <span className="text-muted-foreground italic">—</span>}
                </TableCell>
                <TableCell>
                  <div>{inv.guest?.name}</div>
                  {inv.guest?.phone && <div className="text-[10px] text-muted-foreground">{inv.guest.phone}</div>}
                </TableCell>
                <TableCell className="font-mono-plex" data-testid={`invoice-room-${inv.number}`}>
                  {inv.room_number ? (
                    <div>
                      <div className="font-medium">#{inv.room_number}</div>
                      <div className="text-[10px] text-muted-foreground">{inv.room_type}</div>
                    </div>
                  ) : <span className="text-muted-foreground italic">—</span>}
                </TableCell>
                <TableCell>{inr(inv.room_charges)}</TableCell>
                <TableCell>
                  <div className="flex flex-col">
                    <span>{inr(inv.tax_amount)}</span>
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground" data-testid={`invoice-tax-mode-${inv.number}`}>
                      {inv.tax_inclusive ? "incl. GST" : "excl. GST"} · {inv.tax_rate}%
                    </span>
                  </div>
                </TableCell>
                <TableCell>{inr(inv.extra_charges)}</TableCell>
                <TableCell>{inr(inv.discount)}</TableCell>
                <TableCell className="text-[hsl(var(--success))] font-mono-plex" data-testid={`invoice-deposit-${inv.number}`}>
                  {Number(inv.deposit_applied) > 0 ? `− ${inr(inv.deposit_applied)}` : "—"}
                </TableCell>
                <TableCell className="font-medium">{inr(inv.total)}</TableCell>
                <TableCell className="font-mono-plex" data-testid={`invoice-balance-${inv.number}`}>
                  <span className={Number(inv.balance_due) === 0 ? "text-[hsl(var(--success))]" : ""}>{inr(inv.balance_due)}</span>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground font-mono-plex" data-testid={`invoice-date-${inv.number}`}>
                  {(inv.created_at || "").slice(0, 10)}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={inv.status === "paid" ? "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]" : "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]"}>
                    {inv.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" onClick={() => print(inv)} title="Print" data-testid={`invoice-print-${inv.number}`}><PrinterIcon size={14} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => print(inv, true)} title="Split print (rooms / food / extras on separate pages)" data-testid={`invoice-print-split-${inv.number}`}><FilesIcon size={14} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => downloadPdf(inv)} title="Download PDF" data-testid={`invoice-pdf-${inv.number}`}><FilePdfIcon size={14} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => sendEmail(inv)} title="Email invoice (PDF attached)" data-testid={`invoice-email-${inv.number}`}><EnvelopeIcon size={14} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => sendWhatsApp(inv)} title="WhatsApp invoice" data-testid={`invoice-wa-${inv.number}`}><WhatsappLogoIcon size={14} /></Button>
                    {inv.status !== "paid" && (
                      <>
                        <Button size="icon" variant="ghost" onClick={() => setEditing(inv)} title="Edit" data-testid={`invoice-edit-${inv.number}`}><PencilSimpleIcon size={14} /></Button>
                        <Button size="sm" onClick={() => openMarkPaid(inv)} data-testid={INV.markPaidBtn}>Mark paid</Button>
                        {isMaster && <Button size="icon" variant="ghost" onClick={() => remove(inv)} title="Delete (owner only)" data-testid={`invoice-delete-${inv.number}`}><TrashIcon size={14} /></Button>}
                      </>
                    )}
                    {inv.status === "paid" && isAdmin && (
                      <Button size="icon" variant="ghost" onClick={() => setEditing(inv)} title="Edit paid invoice (admin only)" data-testid={`invoice-edit-paid-${inv.number}`}><PencilSimpleIcon size={14} /></Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={14} className="text-center py-10 text-muted-foreground"><ReceiptIcon size={28} className="mx-auto mb-2 opacity-40" />{items.length === 0 ? "No invoices yet." : "No invoices match your search."}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {editing && (
        <Dialog open onOpenChange={(v) => !v && setEditing(null)}>
          <EditInvoiceDialog invoice={editing} onSaved={() => { setEditing(null); load(); }} onClose={() => setEditing(null)} />
        </Dialog>
      )}

      {payDialog && (
        <Dialog open onOpenChange={(v) => !v && setPayDialog(null)}>
          <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="invoice-mark-paid-dialog">
            <DialogHeader>
              <DialogTitle>Record payment · {payDialog.inv.number}</DialogTitle>
              <p className="text-xs text-muted-foreground mt-1">Guest: <strong>{payDialog.inv.guest?.name || "—"}</strong> · Amount: <strong className="font-mono-plex">₹{Number(payDialog.inv.total || 0).toLocaleString("en-IN")}</strong></p>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs mb-2 block">Payment mode</Label>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {[
                    { key: "cash", label: "Cash", tid: "pay-mode-cash" },
                    { key: "upi", label: "UPI", tid: "pay-mode-upi" },
                    { key: "card", label: "Card", tid: "pay-mode-card" },
                    { key: "bank", label: "Bank", tid: "pay-mode-bank" },
                    { key: "travels", label: "Travels", tid: "pay-mode-travels" },
                    { key: "other", label: "Other", tid: "pay-mode-other" },
                  ].map((m) => (
                    <button
                      key={m.key}
                      type="button"
                      onClick={() => setPayDialog({ ...payDialog, mode: m.key })}
                      className={`py-3 rounded-md border text-sm transition-all ${payDialog.mode === m.key ? "border-primary bg-primary/10 text-primary font-medium" : "border-border hover:border-primary/40"}`}
                      data-testid={m.tid}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
              {(payDialog.mode === "upi" || payDialog.mode === "card" || payDialog.mode === "bank" || payDialog.mode === "travels") && (
                <div>
                  <Label className="text-xs">{payDialog.mode === "upi" ? "UPI reference / UTR" : payDialog.mode === "card" ? "Card txn id (last 4)" : payDialog.mode === "travels" ? "Travel agent / voucher no." : "Bank UTR"}</Label>
                  <Input
                    value={payDialog.reference}
                    onChange={(e) => setPayDialog({ ...payDialog, reference: e.target.value })}
                    placeholder="optional but recommended"
                    data-testid="pay-reference"
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPayDialog(null)}>Cancel</Button>
              <Button onClick={confirmMarkPaid} data-testid="pay-confirm">Record payment</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
